/**
 * English interface text. Other languages must translate everything except `admin` (see ../index.ts).
 * Placeholders like {{name}} must be kept exactly as they are in every translation.
 */
const contractor = {
  common: {
    cancel: 'Cancel',
    save: 'Save',
    retry: 'Retry',
    download: 'Download',
    continue: 'Continue',
    yes: 'Yes',
    no: 'No',
    genericError: 'Something went wrong. Please try again.',
    networkError: "Couldn't reach DubForge. Check your internet connection.",
  },
  app: {
    tagline: 'Dub assignments, files and reviews in one place.',
    startupFailed: "DubForge couldn't start",
    notFoundTitle: 'Page not found',
    notFoundBody: "That page doesn't exist, or you don't have access to it.",
    goHome: 'Go home',
    crashTitle: 'Something went wrong',
    crashBody: 'Try reloading the page.',
    reload: 'Reload',
  },
  language: {
    label: 'Language',
    change: 'Change language',
  },
  welcome: {
    title: 'Welcome to DubForge',
    body: 'Choose your language. DubForge and your Discord notifications will use it.',
    changeLater: 'You can change it any time from the language menu at the top right.',
  },
  nav: {
    myAssignments: 'My assignments',
    openMenu: 'Open menu',
    account: 'Account',
    notificationEmail: 'Notification email',
    signOut: 'Sign out',
    roleAdmin: 'Admin',
    roleContractor: 'Dubber',
    lightMode: 'Light mode',
    darkMode: 'Dark mode',
  },
  login: {
    signIn: 'Sign in with Discord',
    onlyInvited: 'Only Discord accounts added by the channel owner can sign in.',
    signingIn: 'Signing you in…',
    backToSignIn: 'Back to sign in',
    failed: 'Sign-in failed. Please try again.',
  },
  authErrors: {
    not_invited: "This Discord account hasn't been added to DubForge. Ask the channel owner to add you.",
    access_off: 'Your DubForge access has been turned off.',
    discord_only: 'You can only sign in with Discord.',
  },
  errors: {
    not_signed_in: 'Your session has ended. Please sign in again.',
    access_off: 'Your DubForge access has been turned off.',
    admins_only: "You don't have permission to do that.",
    not_found: "That item doesn't exist or is no longer available.",
    invalid_request: 'Something in that request was not valid. Please check and try again.',
    conflict: 'That already exists.',
    already_approved: 'This assignment has already been approved, so no more uploads are needed.',
    not_audio: 'Only WAV or MP3 files can be uploaded. Upload only your voice track.',
    upload_incomplete: "The upload didn't finish. Please try again.",
    discord_unreachable: "Couldn't reach Discord. Please try again later.",
    server_error: 'Something went wrong on our side. Please try again.',
  },
  notifications: {
    title: 'Notifications',
    openWithCount: 'Notifications ({{count}} unread)',
    markAllRead: 'Mark all as read',
    empty: 'Nothing yet.',
  },
  emailSettings: {
    body: 'We email you when you get a new assignment and when your work has been reviewed.',
    label: 'Email address',
    fromDiscord: 'Taken from your Discord account. Change it if you prefer another address.',
    saved: 'Email address saved',
    invalid: 'Enter a valid email address.',
    missing: 'No email address yet. Add one to get notifications.',
  },
  status: {
    Assigned: 'Assigned',
    InProgress: 'In progress',
    Submitted: 'Waiting for review',
    ChangesRequested: 'Changes requested',
    Approved: 'Approved',
  },
  assetKind: {
    package: 'Dubbing package (video + voice + SFX)',
    video: 'Video (720p)',
    sfx: 'SFX / music track',
    voice: 'Voice lines track',
    script: 'Script',
    other: 'Other',
  },
  translation: {
    machine: 'Machine-translated',
    showOriginal: 'Show original',
    showTranslation: 'Show translation',
  },
  uploader: {
    progress: '{{percent}}% of {{size}}',
    queued: 'Waiting to upload: {{count}}',
    keepOpen: 'Keep this tab open until it finishes.',
    cancelled: 'Upload cancelled',
    done: 'Uploaded {{name}}',
    failed: 'Upload failed: {{error}}',
  },
  my: {
    greeting: 'Hi, {{name}}',
    subtitle: 'Your dub assignments.',
    emptyTitle: 'No assignments yet',
    emptyBody: "When you're given a video you'll get a Discord message, and it will appear here.",
    todo: 'To do',
    waiting: 'Waiting for review',
    done: 'Done',
    updated: 'Updated {{when}}',
  },
  assignment: {
    back: 'My assignments',
    dubIn: 'Dub: {{language}}',
    paid: 'Paid',
    next: {
      Assigned: 'Download the source files, record your voice, then upload your voice track here.',
      InProgress: 'When your voice track is ready, upload it below.',
      Submitted: "Your dub has been sent for review. You'll get a Discord message when it's reviewed.",
      ChangesRequested: 'Changes were requested. Read the feedback below, then upload a new version.',
      Approved: 'Approved. Thank you!',
    },
    notes: 'Notes',
    sourceFiles: 'Source files',
    noFiles: 'No files have been uploaded yet.',
    fileInfo: '{{kind}} · {{size}}',
    packageHint: 'One .mp4 with the video and two audio tracks: track 1 is the original voice lines, track 2 is the SFX.',
    uploadTitle: 'Upload your voice track',
    voiceOnlyTitle: 'Only your voice',
    voiceOnlyBody:
      'Upload only your recorded voice: no music, no sound effects, and none of the original audio. Make sure it is the same length as the original.',
    voiceOnlyConfirm: 'This file contains only voice lines.',
    dropLabel: 'Upload voice track here',
    dropHint: 'WAV or MP3 only.',
    noteForReviewer: 'Note for the reviewer (optional)',
    lengthTitle: "The length doesn't match",
    lengthBody:
      "Your file is {{dub}} long, but the SFX track is {{sfx}}. If your track starts at a different point or is cut short, it won't line up with the video. Upload it anyway?",
    uploadAnyway: 'Upload anyway',
    chooseAnother: 'Choose another file',
    versions: 'Uploaded versions',
    nothingUploaded: 'Nothing uploaded yet.',
    version: 'Version {{version}}',
    reviewApproved: 'Approved',
    reviewChanges: 'Changes requested',
    reviewPending: 'Waiting for review',
    yourNote: 'Your note:',
    feedback: 'Feedback',
    listen: 'Listen',
    uploadedOn: '{{date}} · {{size}}',
    length: 'Length {{duration}}',
  },
};

/** Admin-only screens. English only; other languages fall back to these. */
const admin = {
  nav: {
    statusBoard: 'Status board',
    projects: 'Projects',
    contractors: 'Contractors',
    payments: 'Payments',
  },
  common: {
    delete: 'Delete',
    remove: 'Remove',
    edit: 'Edit',
    newProject: 'New project',
    createProject: 'Create a project',
  },
  status: { Submitted: 'Needs review' },
  board: {
    title: 'Status board',
    subtitle: 'Every active project and language at a glance.',
    activeProjects: 'Active projects',
    needsReview: 'Needs your review',
    inProgress: 'In progress',
    approved: 'Approved',
    emptyTitle: 'No active projects',
    emptyBody: 'Create a project for a video, upload its SFX track and assign dubbers.',
    project: 'Project',
    languages: 'Languages',
    noOneAssigned: 'No one assigned yet',
    openAssignment: '{{name}}: open assignment',
    toReview: '{{count}} to review',
    changesCount: '{{count}} changes',
    unassignedCount: '{{count}} unassigned',
    assignDubber: 'Assign dubber',
    openProject: 'Open project',
    showLanguages: 'Show languages',
    hideLanguages: 'Hide languages',
  },
  contractors: {
    title: 'Contractors',
    subtitle: 'People who can sign in with Discord and take dub assignments.',
    add: 'Add contractor',
    addTitle: 'Add a contractor',
    editTitle: 'Edit {{name}}',
    added: '{{name}} added. They can now sign in with Discord.',
    updated: 'Contractor updated',
    languagesTheyDub: 'Languages they dub',
    defaultRate: 'Default rate per video',
    emptyTitle: 'No contractors yet',
    emptyBody:
      "Add the dubbers you work with by their Discord account. They'll sign in with Discord and only see their own assignments.",
    addFirst: 'Add your first contractor',
    colContractor: 'Contractor',
    colLanguages: 'Languages',
    colRate: 'Default rate',
    colEmail: 'Notification email',
    colAccess: 'Access',
    colActions: 'Actions',
    active: 'Active',
    off: 'Off',
    canSignIn: '{{name}} can sign in again',
    cannotSignIn: '{{name}} can no longer sign in',
    testEmail: 'Send a test email',
    testEmailSent: 'Test email sent. Refresh in a minute to see whether it got through.',
    emailFailing: 'Emails failing',
    emailFailingDefault: 'Emails to this person are failing',
    emailOk: 'Emails OK',
    emailUntested: 'Not tested yet',
    noEmail: 'No email yet. They need to sign in once.',
  },
  picker: {
    label: 'Discord user ID',
    helper: 'In Discord: Settings › Advanced › turn on Developer Mode, then right-click them › Copy User ID.',
    invalid: 'A Discord user ID is a long number, like 631239205929877514.',
    duplicate: 'That person is already a contractor.',
  },
  inputs: {
    languages: 'Languages',
    language: 'Language',
    theirLanguages: 'Their languages',
    allLanguages: 'All languages',
    rate: 'Rate',
    currency: 'Currency',
    amountInvalid: 'Enter an amount like 25 or 25.50',
  },
  projects: {
    title: 'Projects',
    subtitle: 'One project per video. Upload the source audio, then assign a dubber per language.',
    editProject: 'Edit project',
    create: 'Create project',
    showArchived: 'Show archived ({{count}})',
    archived: 'Archived',
    emptyTitle: 'No projects yet',
    emptyBody: 'Paste a YouTube link to start a dub project.',
    noDubbers: 'No dubbers assigned',
    approvedProgress: 'Approved: {{approved}}/{{total}}',
    youtubeLink: 'YouTube link',
    youtubeInvalid: "That doesn't look like a YouTube video link",
    youtubeHint: 'Unlisted videos work too.',
    titleLabel: 'Title',
    titleFromYoutube: 'Filled in from YouTube. Edit it if you like.',
    notesLabel: 'Notes for dubbers',
    notesPlaceholder: 'Tone, pronunciations, names to keep in English, delivery deadline…',
    notesTranslated: "Dubbers see these translated into their language, with your original one click away.",
    thumbnailAlt: 'Video thumbnail',
    created: 'Project created. Next, upload the video, voice lines and SFX tracks.',
    updated: 'Project updated',
  },
  project: {
    openYoutube: 'Open on YouTube',
    archive: 'Archive',
    restore: 'Restore',
    archivedToast: 'Project archived',
    restoredToast: 'Project restored',
    notesTitle: 'Notes for dubbers',
    dubs: 'Dubs',
    assign: 'Assign dubber',
    noAssignments:
      'No one is assigned yet. Each language gets its own assignment, and the dubber is messaged on Discord.',
    colLanguage: 'Language',
    colDubber: 'Dubber',
    colStatus: 'Status',
    colRate: 'Rate',
    colPaid: 'Paid',
    sourceFiles: 'Source files',
    sourceFilesBody: 'Everything dubbers can download for this video.',
    startWithSfx: 'Start with the SFX / music track (the mix without your voice).',
    fileType: 'File type',
    packageTitle: 'Dubbing package',
    packageInputVideo: 'Video',
    packageInputVoice: 'Voice lines track',
    packageInputSfx: 'SFX track',
    packageInputMissing: 'Missing: upload it below with File type “{{kind}}”.',
    packageBuilding: 'Building the package…',
    packageReady: 'Ready',
    packageWaiting: 'Waiting for the files marked above.',
    packageFailed: 'Build failed: {{error}}',
    packageRebuild: 'Rebuild',
    packageNotifyNote: 'Dubbers you assign are notified, and can see the project, once the package is ready.',
    videoProcessing: 'Processing your video upload…',
    videoUploadStarted: 'Upload complete. Processing the video…',
    videoFailed: 'Couldn’t get the video: {{error}}',
    dropLabel: 'Drop files here or click to choose',
    uploadHint: 'WAV, FLAC, MP3, PDF or anything else. Large files are fine.',
    deleteFileTitle: 'Delete file?',
    deleteFileBody: "Dubbers will no longer be able to download it. This can't be undone.",
    fileDeleted: 'File deleted',
    fileInfo: '{{kind}} · {{size}} · {{when}}',
    assignTitle: 'Assign a dubber',
    contractorLabel: 'Contractor',
    addContractorFirst: 'Add a contractor first on the Contractors page.',
    rateThisVideo: 'Rate for this video',
    assignButton: 'Assign',
    assigned: 'Assigned. {{name}} will get a Discord DM.',
  },
  assignment: {
    back: 'Project',
    next: {
      Assigned: 'Waiting for the dubber to start.',
      InProgress: 'The dubber has downloaded the files and is working on it.',
      Submitted: 'A new version is waiting for your review.',
      ChangesRequested: 'Waiting for the dubber to upload a revised version.',
      Approved: 'Approved. Nice work!',
    },
    dubberNote: "Dubber's note:",
    approve: 'Approve',
    requestChanges: 'Request changes',
    approvedToast: 'Approved. The dubber has been notified.',
    changesTitle: 'Request changes to version {{version}}',
    changesLabel: 'What needs changing?',
    changesPlaceholder: 'e.g. 2:10 – 2:25 is out of sync; the name is pronounced…',
    changesHelper: "Sent to the dubber as a Discord DM, translated into their language.",
    sendToDubber: 'Send to dubber',
    changesToast: 'Changes requested',
    payment: 'Payment',
    paidOn: 'Paid {{date}}',
    saveRate: 'Save rate',
    rateUpdated: 'Rate updated',
    removeAssignment: 'Remove assignment',
    removeTitle: 'Remove this assignment?',
    removeBody: '{{name}} will lose access to this project. Uploaded files are kept in storage.',
    removed: 'Assignment removed',
    lengthWarning: "Length differs from the SFX track by {{diff}}",
  },
  mix: {
    title: 'Mix with SFX',
    open: 'Open mixer',
    previewHint: 'Plays your SFX track and this voice track together. Adjust the levels until the balance sounds right, then create the mix.',
    loading: 'Loading audio…',
    play: 'Play',
    pause: 'Pause',
    voiceLevel: 'Voice level',
    sfxLevel: 'SFX level',
    reset: 'Reset levels',
    normalize: 'Normalize the finished mix to -14 LUFS',
    normalizeHelp:
      "Turns the whole mix up or down evenly so it lands on YouTube's loudness target. The balance you set above stays exactly the same.",
    create: 'Create mix',
    recreate: 'Create mix with these levels',
    processing: 'Mixing… this usually takes under a minute.',
    ready: 'Mix ready',
    readyDetails: '{{lufs}} LUFS · voice {{voice}} dB · SFX {{sfx}} dB',
    failed: 'The mix failed: {{error}}',
    downloadWav: 'Download WAV',
    downloadMp3: 'Download MP3',
    listen: 'Listen to mix',
    noSfx: 'Upload an SFX track to this project to mix this version.',
    previewFailed: "Couldn't play the preview. The mix itself can still be created.",
  },
  payments: {
    title: 'Payments',
    subtitle: "Track what you owe dubbers. DubForge doesn't move money; mark jobs paid once you've paid them.",
    owed: 'Owed (approved, unpaid)',
    paidToDate: 'Paid to date',
    owedPerContractor: 'Owed per contractor',
    caughtUp: 'All caught up 🎉',
    filterOwed: 'Owed',
    filterUnpaid: 'All unpaid',
    filterPaid: 'Paid',
    filterAll: 'Everything',
    emptyTitle: 'Nothing here',
    emptyBody: 'No assignments match this filter.',
    colProject: 'Project',
    colLanguage: 'Language',
    colContractor: 'Contractor',
    colStatus: 'Status',
    colAmount: 'Amount',
    colPaid: 'Paid',
  },
};

export const en = { ...contractor, admin };

/** What every language must translate. */
export type ContractorMessages = typeof contractor & { admin?: Partial<typeof admin> };

/** Same keys as English, but every value is a string (for checking translations' shape). */
export type Translation<T> = { [K in keyof T]: T[K] extends string ? string : Translation<T[K]> };
export type ContractorTranslation = Translation<typeof contractor>;
